import { useEffect } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { resolveLogoUrl } from "@/lib/branding";
import { imgVariant } from "@/lib/tileSrc";

const DEFAULT_TITLE = "Allsale Classified — New Zealand's premium classifieds";
const DEFAULT_DESC = "Buy and sell cars, property, electronics, jobs, pets and more across New Zealand. Kia Ora — post your ad free on Allsale Classified.";
const BRAND_SUFFIX = " · Allsale Classified";

/**
 * Seo — WO 800 §3: SINGLE head writer with replace-by-key semantics.
 *
 * The SSR shell (/api/seo/render) bakes title/description/canonical/OG into
 * <head>; previously react-helmet-async appended a SECOND set on hydration,
 * so crawler snapshots carried duplicate canonical/description tags. Every
 * managed tag is now deduped to exactly ONE element per key: extras are
 * removed, the first survivor is updated in place.
 */

// Keep exactly one element matching `selector`; create it if missing.
function upsertOne(selector, create) {
  const all = document.head.querySelectorAll(selector);
  for (let i = 1; i < all.length; i += 1) all[i].remove();
  let el = all[0];
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function upsertMeta(attrName, attrValue, content) {
  const el = upsertOne(`meta[${attrName}="${attrValue}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute(attrName, attrValue);
    return m;
  });
  if (el.getAttribute("content") !== content) el.setAttribute("content", content);
}

function removeAll(selector) {
  document.head.querySelectorAll(selector).forEach((n) => n.remove());
}

export default function Seo({ title, description, image, url, type = "website", jsonLd, noindex = false, robots }) {
  const { data: settings = {} } = useSiteSettings();
  // Backend SEO payloads (WO 800 §2/§5) arrive pre-branded — never double-suffix.
  const fullTitle = !title
    ? DEFAULT_TITLE
    : title === DEFAULT_TITLE || title.includes(BRAND_SUFFIX.trim())
      ? title
      : `${title}${BRAND_SUFFIX}`;
  const desc = description || DEFAULT_DESC;
  // Iter 697 Amendment C — canonical host is the APEX.
  const toCanonicalHost = (u) =>
    (u || "").replace(/^https?:\/\/www\.allsale\.co\.nz/i, "https://allsale.co.nz");
  // noindex pages carry no canonical (WO 800 §4 — the 404 page used to
  // claim canonical=/404, a classic soft-404 signal).
  const canonical = noindex ? "" : toCanonicalHost(url || (typeof window !== "undefined" ? window.location.href : ""));
  const brandLogo = resolveLogoUrl(settings);
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://allsale.co.nz";
  const usingDefaultCard = !image;
  const ogImage = usingDefaultCard ? `${origin}/og-card-1200x630.png` : imgVariant(image, 1200);
  const faviconUrl = settings.favicon_url || brandLogo;

  const ldBlocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const ldPayload = JSON.stringify(ldBlocks);

  // Managed meta/OG/Twitter/canonical/title — exactly one of each.
  useEffect(() => {
    if (typeof document === "undefined") return;
    // Dedupe stray <title> nodes (SSR shell + hydration races).
    const titles = document.head.querySelectorAll("title");
    for (let i = 1; i < titles.length; i += 1) titles[i].remove();
    if (document.title !== fullTitle) document.title = fullTitle;

    upsertMeta("name", "description", desc);
    upsertMeta("name", "theme-color", "#1CA9E0");
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", "Allsale Classified");
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:image", ogImage);
    if (usingDefaultCard) {
      upsertMeta("property", "og:image:width", "1200");
      upsertMeta("property", "og:image:height", "630");
    } else {
      removeAll('meta[property="og:image:width"]');
      removeAll('meta[property="og:image:height"]');
    }
    if (canonical) upsertMeta("property", "og:url", canonical);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", ogImage);

    if (canonical) {
      const link = upsertOne('link[rel="canonical"]', () => {
        const l = document.createElement("link");
        l.setAttribute("rel", "canonical");
        return l;
      });
      if (link.getAttribute("href") !== canonical) link.setAttribute("href", canonical);
    } else {
      removeAll('link[rel="canonical"]');
      removeAll('meta[property="og:url"]');
    }
  }, [fullTitle, desc, canonical, ogImage, type, usingDefaultCard]);

  // JSON-LD — when this page supplies blocks, they REPLACE any SSR-injected
  // untagged blocks (otherwise crawler snapshots carry both sets).
  useEffect(() => {
    if (typeof document === "undefined" || ldBlocks.length === 0) return undefined;
    document.head
      .querySelectorAll('script[type="application/ld+json"]:not([data-allsale-seo])')
      .forEach((n) => n.remove());
    const nodes = ldBlocks.map((block) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-allsale-seo", "1");
      el.textContent = JSON.stringify(block).replace(/</g, "\\u003c");
      document.head.appendChild(el);
      return el;
    });
    return () => {
      nodes.forEach((n) => {
        if (n.parentNode) n.parentNode.removeChild(n);
      });
    };
  }, [ldPayload]);

  // Favicon sync (never removed on unmount).
  useEffect(() => {
    if (typeof document === "undefined" || !faviconUrl) return;
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    if (link.href !== faviconUrl) link.href = faviconUrl;
  }, [faviconUrl]);

  // Iter 720 — per-page noindex. Own tagged node so unmount never removes
  // the host-level preview/staging robots meta managed in App.js.
  useEffect(() => {
    if (typeof document === "undefined" || !noindex) return undefined;
    // Replace any SSR-shell robots meta (untagged) — host-level and
    // page-robots nodes are tagged and survive.
    removeAll('meta[name="robots"]:not([data-allsale-noindex]):not([data-allsale-page-robots]):not([data-allsale-host-robots])');
    const el = document.createElement("meta");
    el.setAttribute("name", "robots");
    el.setAttribute("content", "noindex, nofollow");
    el.setAttribute("data-allsale-noindex", "1");
    document.head.appendChild(el);
    return () => {
      if (el.parentNode) el.parentNode.removeChild(el);
    };
  }, [noindex]);

  // WO 800 §2 — backend-supplied robots directive (e.g. "noindex,follow"
  // for zero-stock categories). Tagged separately from the noindex node.
  useEffect(() => {
    if (typeof document === "undefined" || !robots) return undefined;
    removeAll('meta[name="robots"]:not([data-allsale-noindex]):not([data-allsale-page-robots]):not([data-allsale-host-robots])');
    const el = document.createElement("meta");
    el.setAttribute("name", "robots");
    el.setAttribute("content", robots);
    el.setAttribute("data-allsale-page-robots", "1");
    document.head.appendChild(el);
    return () => {
      if (el.parentNode) el.parentNode.removeChild(el);
    };
  }, [robots]);

  return null;
}
